import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap'
import { MdAdd, MdClose, MdDelete, MdEdit, MdSave } from 'react-icons/md'
import moment from 'moment-timezone'

import { createGoalClientId, normalizeGoalTags } from './goalUtils'
import GoalTargetPhasesEditor from './GoalTargetPhasesEditor'
import { formatTargetPhasePosition, makeGoalTarget, makeRemainderTargetPhase, normalizeGoalTargetPhases } from './goalPhases'
import { CurrentPeriodHeading, getExpectedProgress, getRemainingOperationalDays, TargetProgress } from './GoalProgress'
import { deleteGoal, fetchGoals, fetchGoalTriggerOptions, saveGoal } from './goalsApi'

const makeOption = (label = '') => ({ id: createGoalClientId(), label, value: label })

const makeQuestion = () => ({
    id: createGoalClientId(),
    label: '',
    type: 'normalRadio',
    options: [makeOption('Yes'), makeOption('No')],
})

const makeEmptyGoal = () => ({
    name: '',
    campaignStartDate: moment().format('YYYY-MM-DD'),
    campaignEndDate: moment().add(30, 'days').format('YYYY-MM-DD'),
    evaluationBasis: { type: 'daily', n: 1 },
    triggerMode: 'tasks',
    tags: [],
    triggers: [],
    questions: [],
    targetPhases: [makeRemainderTargetPhase()],
})

const clone = (value) => JSON.parse(JSON.stringify(value))

const formatBasis = (basis) => {
    if (basis?.type === 'daily') return 'Daily'
    if (basis?.type === 'everyNDays') return Number(basis.n) === 1 ? 'Every day' : `Every ${basis.n} days`
    if (basis?.type === 'everyNWeeks') return Number(basis.n) === 1 ? 'Every week' : `Every ${basis.n} weeks`
    if (basis?.type === 'everyNMonths') return Number(basis.n) === 1 ? 'Every month' : `Every ${basis.n} months`
    return ''
}

const formatPhaseSchedule = (phases = []) => phases.map((phase) => (
    phase.kind === 'remainder'
        ? 'rest'
        : `${phase.evaluationCount} evaluation${Number(phase.evaluationCount) === 1 ? '' : 's'}`
)).join(' → ')

function Goals() {
    const [goals, setGoals] = useState([])
    const [triggerOptions, setTriggerOptions] = useState([])
    const [editorGoal, setEditorGoal] = useState(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [message, setMessage] = useState('')

    const timezone = localStorage.getItem('timezone') || moment.tz.guess()

    const refresh = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const [goalData, optionData] = await Promise.all([
                fetchGoals(true),
                fetchGoalTriggerOptions(),
            ])
            setGoals(goalData.map(normalizeGoalTargetPhases))
            setTriggerOptions(optionData)
        } catch (refreshError) {
            setError(refreshError.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        document.title = 'Goals'
        refresh()
    }, [refresh])

    const campaignStarted = useMemo(() => {
        if (!editorGoal?._id) return false
        const savedGoal = goals.find((goal) => goal._id === editorGoal._id) || editorGoal
        return moment().isSameOrAfter(moment.tz(`${savedGoal.campaignStartDate} 02:00`, 'YYYY-MM-DD HH:mm', timezone))
    }, [editorGoal, goals, timezone])

    const updateGoal = (updater) => setEditorGoal((current) => {
        const next = clone(current)
        updater(next)
        return next
    })

    const toggleStamp = (stampId, checked) => updateGoal((goal) => {
        if (checked) goal.triggers.push({ stampId, subtasks: null })
        else goal.triggers = goal.triggers.filter((trigger) => trigger.stampId !== stampId)
    })

    const toggleStampNarrowing = (stampId, narrowed) => updateGoal((goal) => {
        const trigger = goal.triggers.find((candidate) => candidate.stampId === stampId)
        trigger.subtasks = narrowed ? [] : null
    })

    const toggleSubtask = (stampId, subtaskName, checked) => updateGoal((goal) => {
        const trigger = goal.triggers.find((candidate) => candidate.stampId === stampId)
        if (checked) trigger.subtasks.push({ name: subtaskName, subsubs: null })
        else trigger.subtasks = trigger.subtasks.filter((subtask) => subtask.name !== subtaskName)
    })

    const toggleSubtaskNarrowing = (stampId, subtaskName, narrowed) => updateGoal((goal) => {
        const trigger = goal.triggers.find((candidate) => candidate.stampId === stampId)
        const subtask = trigger.subtasks.find((candidate) => candidate.name === subtaskName)
        subtask.subsubs = narrowed ? [] : null
    })

    const toggleSubsubtask = (stampId, subtaskName, subsubtaskName, checked) => updateGoal((goal) => {
        const trigger = goal.triggers.find((candidate) => candidate.stampId === stampId)
        const subtask = trigger.subtasks.find((candidate) => candidate.name === subtaskName)
        if (checked) subtask.subsubs.push(subsubtaskName)
        else subtask.subsubs = subtask.subsubs.filter((name) => name !== subsubtaskName)
    })

    const addQuestion = () => updateGoal((goal) => goal.questions.push(makeQuestion()))

    const removeQuestion = (questionId) => updateGoal((goal) => {
        goal.questions = goal.questions.filter((question) => question.id !== questionId)
        goal.targetPhases.forEach((phase) => {
            phase.targets = phase.targets.filter((target) => target.questionId !== questionId)
            if (phase.targets.length === 0) phase.targets.push(makeGoalTarget())
        })
    })

    const changeQuestionType = (questionId, type) => updateGoal((goal) => {
        const question = goal.questions.find((candidate) => candidate.id === questionId)
        question.type = type
        question.options = type === 'normalRadio'
            ? [makeOption('Yes'), makeOption('No')]
            : [makeOption('1'), makeOption('2'), makeOption('3'), makeOption('4'), makeOption('5')]
        question.options.forEach((option) => { if (type === 'numericRadio') option.value = Number(option.label) })
        goal.targetPhases.forEach((phase) => {
            phase.targets = phase.targets.filter((target) => target.questionId !== questionId)
            if (phase.targets.length === 0) phase.targets.push(makeGoalTarget())
        })
    })

    const setQuestionOptions = (questionId, text) => updateGoal((goal) => {
        const question = goal.questions.find((candidate) => candidate.id === questionId)
        const previousByLabel = new Map(question.options.map((option) => [String(option.label), option]))
        question.options = text.split(/\n|,/).map((label) => label.trim()).filter(Boolean).map((label) => {
            const existing = previousByLabel.get(label)
            return existing || { id: createGoalClientId(), label, value: question.type === 'numericRadio' ? Number(label) : undefined }
        })
    })

    const targetChoices = useMemo(() => {
        if (!editorGoal) return []
        const choices = [
            { value: 'attempts', label: 'Attempts' },
            { value: 'durationMinutes', label: 'Duration (minutes)' },
        ]
        editorGoal.questions.forEach((question) => {
            if (question.type === 'numericRadio') {
                choices.push({ value: `numericSum|${question.id}`, label: `${question.label || 'Untitled question'} — numeric sum` })
            } else {
                question.options.forEach((option) => choices.push({
                    value: `answerCount|${question.id}|${option.id}`,
                    label: `${question.label || 'Untitled question'} — count “${option.label}”`,
                }))
            }
        })
        return choices
    }, [editorGoal])

    const handleSave = async () => {
        if (!editorGoal.campaignStartDate || !editorGoal.campaignEndDate || editorGoal.campaignEndDate < editorGoal.campaignStartDate) {
            setError('Choose a start date and an end date on or after the start date.')
            return
        }
        if (editorGoal.recalculateHistory && !window.confirm('Recalculate completed evaluations with this phase schedule and these targets? Historical Achieved/Missed results may change.')) return
        setSaving(true)
        setError('')
        setMessage('')
        try {
            await saveGoal({ ...editorGoal, tags: normalizeGoalTags(editorGoal.tags) })
            setEditorGoal(null)
            setMessage('Goal saved.')
            await refresh()
        } catch (saveError) {
            setError(saveError.message)
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (goal) => {
        if (!window.confirm(`Delete “${goal.name}”? Its recorded data will be retained but hidden.`)) return
        setError('')
        try {
            await deleteGoal(goal._id)
            setMessage('Goal deleted.')
            await refresh()
        } catch (deleteError) {
            setError(deleteError.message)
        }
    }

    return (
        <div className="AppRootDesktop goals-root">
            <div className="AppRootInnerOverlay">
                <Container fluid className="goals-page">
                    <div className="goals-action-bar">
                        <div>
                            <h1>Goals</h1>
                            <p>Campaign progress from committed work sessions.</p>
                        </div>
                        <Button variant="outline-light" onClick={() => { setError(''); setEditorGoal(makeEmptyGoal()) }}>
                            <MdAdd /> New Goal
                        </Button>
                    </div>

                    <div className="goals-content">
                        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
                        {message && <Alert variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}
                        {loading && <div className="goals-loading"><Spinner animation="border" /></div>}

                        {editorGoal && (
                            <Card className="goal-editor-card">
                                <Card.Body>
                                    <div className="goal-editor-heading">
                                        <h2>{editorGoal._id ? 'Edit Goal' : 'Create Goal'}</h2>
                                        <Button variant="outline-light" className="goal-icon-button" onClick={() => setEditorGoal(null)}><MdClose /></Button>
                                    </div>

                                    <div className="goal-editor-grid">
                                        <Form.Group>
                                            <Form.Label>Goal Name</Form.Label>
                                            <Form.Control value={editorGoal.name} onChange={(event) => updateGoal((goal) => { goal.name = event.target.value })} />
                                        </Form.Group>
                                        <Form.Group controlId="goal-campaign-start-date">
                                            <Form.Label>Campaign Start Date</Form.Label>
                                            <Form.Control type="date" value={editorGoal.campaignStartDate} onChange={(event) => {
                                                const { value } = event.target
                                                updateGoal((goal) => { goal.campaignStartDate = value })
                                            }} />
                                        </Form.Group>
                                        <Form.Group controlId="goal-campaign-end-date">
                                            <Form.Label>Campaign End Date</Form.Label>
                                            <Form.Control type="date" min={editorGoal.campaignStartDate} value={editorGoal.campaignEndDate} onChange={(event) => {
                                                const { value } = event.target
                                                updateGoal((goal) => { goal.campaignEndDate = value })
                                            }} />
                                        </Form.Group>
                                        <Form.Group controlId="goal-evaluation-basis">
                                            <Form.Label>Evaluation Basis</Form.Label>
                                            <div className="goal-basis-row">
                                                <Form.Select disabled={campaignStarted} value={editorGoal.evaluationBasis.type} onChange={(event) => updateGoal((goal) => { goal.evaluationBasis.type = event.target.value; if (event.target.value === 'daily') goal.evaluationBasis.n = 1 })}>
                                                    <option value="daily">Daily</option>
                                                    <option value="everyNDays">Every N days</option>
                                                    <option value="everyNWeeks">Every N weeks</option>
                                                    <option value="everyNMonths">Every N months</option>
                                                </Form.Select>
                                                {editorGoal.evaluationBasis.type !== 'daily' && <Form.Control id="goal-evaluation-interval" aria-label="Evaluation interval" disabled={campaignStarted} type="number" min="1" step="1" value={editorGoal.evaluationBasis.n} onChange={(event) => updateGoal((goal) => { goal.evaluationBasis.n = event.target.value })} />}
                                            </div>
                                        </Form.Group>
                                    </div>
                                    <Form.Text>Changing campaign dates updates the evaluation period. Recorded sessions and completed evaluation results are preserved.</Form.Text>

                                    <section className="goal-editor-section">
                                        <h3>Trigger Set</h3>
                                        <Form.Group controlId="goal-tracking-mode">
                                            <Form.Label>Tracking mode</Form.Label>
                                            <Form.Select value={editorGoal.triggerMode || 'tasks'} onChange={(event) => { const value = event.target.value; updateGoal((goal) => { goal.triggerMode = value }) }}>
                                                <option value="tasks">Task-based</option>
                                                <option value="tags">Tag-based</option>
                                            </Form.Select>
                                        </Form.Group>
                                        {editorGoal.triggerMode === 'tags' ? (
                                            <Form.Group controlId="goal-tags">
                                                <Form.Label>Subtask tags</Form.Label>
                                                <Form.Control as="textarea" rows={2} value={Array.isArray(editorGoal.tags) ? editorGoal.tags.join(', ') : editorGoal.tags || ''} onChange={(event) => { const value = event.target.value; updateGoal((goal) => { goal.tags = value }) }} placeholder="study, research" />
                                                <Form.Text>Separate tags with commas or new lines. Sessions count once when the subtask has any of these tags, including newly created subtasks under any task. Tags ignore case and whitespace.</Form.Text>
                                            </Form.Group>
                                        ) : <>
                                        <p>Select at least one stamp. Narrow a selected branch only when the whole parent should not trigger it.</p>
                                        <div className="goal-trigger-list">
                                            {triggerOptions.map((stampOption) => {
                                                const trigger = editorGoal.triggers.find((candidate) => candidate.stampId === stampOption.stampId)
                                                return (
                                                    <div className="goal-trigger-stamp" key={stampOption.stampId}>
                                                        <Form.Check type="checkbox" checked={Boolean(trigger)} onChange={(event) => toggleStamp(stampOption.stampId, event.target.checked)} label={`${stampOption.label} (${stampOption.stampId})`} />
                                                        {trigger && stampOption.subtasks.length > 0 && (
                                                            <div className="goal-trigger-children">
                                                                <Form.Check type="switch" checked={trigger.subtasks !== null} onChange={(event) => toggleStampNarrowing(stampOption.stampId, event.target.checked)} label="Only selected subtasks" />
                                                                {trigger.subtasks !== null && stampOption.subtasks.map((subtaskOption) => {
                                                                    const selectedSubtask = trigger.subtasks.find((candidate) => candidate.name === subtaskOption.name)
                                                                    return (
                                                                        <div className="goal-trigger-subtask" key={subtaskOption.name}>
                                                                            <Form.Check checked={Boolean(selectedSubtask)} onChange={(event) => toggleSubtask(stampOption.stampId, subtaskOption.name, event.target.checked)} label={subtaskOption.name} />
                                                                            {selectedSubtask && subtaskOption.subsubs.length > 0 && (
                                                                                <div className="goal-trigger-subsubs">
                                                                                    <Form.Check type="switch" checked={selectedSubtask.subsubs !== null} onChange={(event) => toggleSubtaskNarrowing(stampOption.stampId, subtaskOption.name, event.target.checked)} label="Only selected subsubtasks" />
                                                                                    {selectedSubtask.subsubs !== null && subtaskOption.subsubs.map((subsubtaskName) => (
                                                                                        <Form.Check key={subsubtaskName} checked={selectedSubtask.subsubs.includes(subsubtaskName)} onChange={(event) => toggleSubsubtask(stampOption.stampId, subtaskOption.name, subsubtaskName, event.target.checked)} label={subsubtaskName} />
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        </>}
                                    </section>

                                    <section className="goal-editor-section">
                                        <h3>Form</h3>
                                        <div className="goal-automatic-items">
                                            <div><strong>Attempts</strong><span>Automatically recorded</span></div>
                                            <div><strong>Duration</strong><span>Automatically recorded in minutes</span></div>
                                        </div>
                                        {editorGoal.questions.map((question, questionIndex) => (
                                            <div className="goal-question-editor" key={question.id}>
                                                <div className="goal-question-heading"><strong>Question {questionIndex + 1}</strong><Button variant="outline-danger" className="goal-icon-button" onClick={() => removeQuestion(question.id)}><MdDelete /></Button></div>
                                                <div className="goal-editor-grid">
                                                    <Form.Group>
                                                        <Form.Label>Question</Form.Label>
                                                        <Form.Control value={question.label} onChange={(event) => updateGoal((goal) => { goal.questions.find((candidate) => candidate.id === question.id).label = event.target.value })} />
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <Form.Label>Type</Form.Label>
                                                        <Form.Select value={question.type} onChange={(event) => changeQuestionType(question.id, event.target.value)}>
                                                            <option value="normalRadio">Normal Radio</option>
                                                            <option value="numericRadio">Numeric Radio</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </div>
                                                <Form.Group>
                                                    <Form.Label>{question.type === 'numericRadio' ? 'Numeric choices' : 'Choices'} — comma or line separated</Form.Label>
                                                    <Form.Control as="textarea" rows={2} value={question.options.map((option) => option.label).join(', ')} onChange={(event) => setQuestionOptions(question.id, event.target.value)} />
                                                </Form.Group>
                                            </div>
                                        ))}
                                        <Button variant="outline-light" className="goal-add-question-button" onClick={addQuestion}><MdAdd /> Add Question</Button>
                                    </section>

                                    <GoalTargetPhasesEditor
                                        goal={editorGoal}
                                        targetChoices={targetChoices}
                                        timezone={timezone}
                                        campaignStarted={campaignStarted}
                                        onUpdate={updateGoal}
                                    />

                                    <div className="goal-editor-footer">
                                        <Button variant="success" disabled={saving} onClick={handleSave}>{saving ? <Spinner size="sm" animation="border" /> : <MdSave />} Save Goal</Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        {!loading && goals.length === 0 && !editorGoal && <div className="goals-empty"><h2>No goals yet</h2><p>Create a campaign to begin measuring committed work.</p></div>}

                        <div className="goal-card-grid">
                            {goals.map((goal) => (
                                <Card className="goal-card" key={goal._id}>
                                    <Card.Body>
                                        <div className="goal-card-heading">
                                            <div><span className={`goal-status goal-status-${goal.progress?.status}`}>{goal.progress?.status}</span><h2>{goal.name}</h2></div>
                                            <div className="goal-card-actions">
                                                <Button variant="outline-light" className="goal-icon-button" aria-label={`Edit ${goal.name}`} onClick={() => { setError(''); setEditorGoal(clone(goal)) }}><MdEdit /></Button>
                                                <Button variant="outline-danger" className="goal-icon-button" onClick={() => handleDelete(goal)}><MdDelete /></Button>
                                            </div>
                                        </div>
                                        <div className="goal-meta">
                                            <span>{goal.campaignStartDate} → {goal.campaignEndDate}</span>
                                            <span>{formatBasis(goal.evaluationBasis)}</span>
                                            <span>{goal.triggerMode === 'tags' ? `Tag-based: ${normalizeGoalTags(goal.tags).join(', ')}` : 'Task-based'}</span>
                                            <span>{formatPhaseSchedule(goal.targetPhases)}</span>
                                        </div>
                                        {goal.progress?.current ? (
                                            <div className="goal-current-progress">
                                                <CurrentPeriodHeading period={goal.progress.current} timezone={timezone} />
                                                {(goal.progress.current.targetProgress || []).map((target) => <TargetProgress
                                                    key={target.id}
                                                    target={target}
                                                    questions={goal.questions}
                                                    expectedPercentage={getExpectedProgress(goal.progress.current, timezone)}
                                                    remainingDays={getRemainingOperationalDays(goal.progress.current, timezone)}
                                                />)}
                                                <div className={`goal-achievement ${goal.progress.current.achieved ? 'achieved' : ''}`}>{goal.progress.current.achieved ? 'Goal achieved' : `${Math.round(goal.progress.current.overallPercentage || 0)}% overall`}</div>
                                            </div>
                                        ) : <div className="goal-no-current">{goal.progress?.status === 'upcoming' ? 'Campaign has not started.' : 'Campaign completed.'}</div>}

                                        {(goal.progress?.history || []).length > 0 && (
                                            <details className="goal-history">
                                                <summary>Previous periods ({goal.progress.history.length})</summary>
                                                {goal.progress.history.map((period) => (
                                                    <div className="goal-history-period" key={period._id}>
                                                        <div className="goal-period-heading">
                                                            <div className="goal-history-result">
                                                                <strong>{period.achieved ? 'Achieved' : 'Missed'}</strong>
                                                                {period.targetPhase && <small>{formatTargetPhasePosition(period.targetPhase)}</small>}
                                                            </div>
                                                            <span>{moment(period.periodStart).format('D MMM')} – {moment(period.periodEnd).format('D MMM YYYY')}</span>
                                                        </div>
                                                        {(period.targetProgress || []).map((target) => <TargetProgress key={target.id} target={target} questions={period.questionSnapshot || goal.questions} />)}
                                                    </div>
                                                ))}
                                            </details>
                                        )}
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    </div>
                </Container>
            </div>
        </div>
    )
}

export default Goals
